import { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';

const nav = [
  { section: 'Visío geral', items: [{ to: '/dashboard', label: 'Dashboard', icon: 'dashboard', end: true }] },
  {
    section: 'Estúdio',
    items: [
      { to: '/modelos', label: 'Modelos', icon: 'inventory_2' },
      { to: '/formatos', label: 'Formatos', icon: 'straighten' },
      { to: '/impressoras', label: 'Impressoras', icon: 'hardware' },
      { to: '/templates', label: 'Etiquetas', icon: 'description' },
      { to: '/biblioteca-imagens', label: 'Biblioteca', icon: 'photo_library' },
    ],
  },
  {
    section: 'Dados',
    items: [
      { to: '/fontes-de-dados', label: 'Fontes de Dados', icon: 'database' },
      { to: '/formulas', label: 'Fórmulas', icon: 'function' },
      { to: '/contadores', label: 'Contadores', icon: 'counter_1' },
    ],
  },
  {
    section: 'operação',
    items: [
      { to: '/impressao', label: 'impressão', icon: 'print' },
      { to: '/historico', label: 'Histórico', icon: 'history' },
    ],
  },
  {
    section: 'Administração',
    items: [
      { to: '/usuarios', label: 'Usuários', icon: 'group' },
      { to: '/categorias', label: 'Categorias', icon: 'label' },
      { to: '/permissoes', label: 'Permissões', icon: 'admin_panel_settings' },
      { to: '/documentacao', label: 'Documentação API', icon: 'code' },
    ],
  },
  {
    section: 'Conta',
    items: [
      { to: '/baixar-app', label: 'Baixar App', icon: 'download' },
      { to: '/configuracoes', label: 'Configurações', icon: 'settings' },
      { to: '/ajuda', label: 'Central de Ajuda', icon: 'help_outline' },
    ],
  },
];

const headerActions = [
  { icon: 'notifications', label: 'Notificações', badge: '1', hideOnMobile: false },
  { icon: 'print', label: 'Fila de impressão', hideOnMobile: false },
  { icon: 'group_add', label: 'Convidar equipe', hideOnMobile: true },
  { icon: 'extension', label: 'Integrações', hideOnMobile: true },
  { icon: 'settings', label: 'Preferências', hideOnMobile: true },
  { icon: 'help', label: 'Ajuda', hideOnMobile: false },
  { icon: 'apps', label: 'Apps', hideOnMobile: false },
];

const themeOptions = [
  {
    id: 'studio',
    label: 'Estúdio',
    icon: 'style',
    description: 'Tema principal do dashboard com laranja, preto e branco.',
    preview: ['#ffffff', '#171717', '#f97316'],
  },
  {
    id: 'login',
    label: 'Login',
    icon: 'stacked_line_chart',
    description: 'Paleta clara inspirada na tela de entrada do produto.',
    preview: ['#eef2f7', '#0f172a', '#f97316'],
  },
  {
    id: 'dark',
    label: 'Escuro',
    icon: 'dark_mode',
    description: 'Visual noturno com contraste mais forte para operação contínua.',
    preview: ['#0f1522', '#fbfcff', '#ff9a52'],
  },
  {
    id: 'light-minimal',
    label: 'Claro',
    icon: 'light_mode',
    description: 'Navbar e sidebar brancas, com navegação leve e visual minimalista.',
    preview: ['#ffffff', '#4b5563', '#f97316'],
  },
  {
    id: 'contrast-deep',
    label: 'Contraste',
    icon: 'contrast',
    description: 'Chrome escuro sofisticado para destacar ainda mais a área operacional.',
    preview: ['#111827', '#d1d5db', '#f97316'],
  },
  {
    id: 'blue-refined',
    label: 'Azul',
    icon: 'palette',
    description: 'Navegação azul clara com leitura monocromática e contraste refinado.',
    preview: ['#e0f2fe', '#0369a1', '#bae6fd'],
  },
  {
    id: 'hybrid-accent',
    label: 'Híbrido',
    icon: 'splitscreen',
    description: 'Base branca com acento azul fino para manter o app limpo e orientado à operação.',
    preview: ['#ffffff', '#4b5563', '#075985'],
  },
];

const roleLabels = {
  ADMIN: 'Administrador',
  DESIGNER: 'Designer',
  OPERATOR: 'Operador',
};

const THEME_STORAGE_KEY = 'studio-theme';

function getUserInitials(user) {
  const source = user?.name?.trim() || user?.email?.trim() || 'EA';
  const parts = source.split(/[\s@._-]+/).filter(Boolean);

  return parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('') || 'EA';
}

function matchesPath(pathname, path, end = false) {
  if (end) return pathname === path;
  return pathname === path || pathname.startsWith(`${path}/`);
}

function HeaderActionButton({ icon, label, badge, hideOnMobile = false }) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      className={`studio-shell-header-action relative flex h-7.5 w-7.5 items-center justify-center rounded-full transition-colors duration-200 ${hideOnMobile ? 'hidden lg:flex' : ''
        }`}
    >
      <span className="material-symbols-outlined text-[17px]">{icon}</span>
      {badge ? (
        <span className="absolute -right-1 -top-1 min-w-[15px] rounded-full bg-primary px-1 py-0.5 text-center text-[8px] font-bold leading-none text-on-primary">
          {badge}
        </span>
      ) : null}
    </button>
  );
}

function UserMenuAction({ icon, label, detail, active = false, tone = 'default', onClick }) {
  const baseClass =
    'flex w-full items-start gap-3 rounded-2xl px-3 py-2.5 text-left transition-all duration-200';
  const toneClass =
    tone === 'danger'
      ? 'text-error hover:bg-error-container/50'
      : active
        ? 'bg-primary-container text-on-primary-container shadow-[0_12px_24px_rgba(249,115,22,0.18)]'
        : 'text-on-surface-variant hover:bg-surface-container-low hover:text-on-surface';

  return (
    <button type="button" onClick={onClick} className={`${baseClass} ${toneClass}`}>
      <span className="material-symbols-outlined mt-0.5 text-[18px]">{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13px] font-semibold">{label}</span>
        <span className={`mt-0.5 block text-[11px] leading-5 ${active ? 'text-on-primary-container opacity-80' : 'text-on-surface-variant'}`}>
          {detail}
        </span>
      </span>
    </button>
  );
}

function ThemeOptionButton({ option, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-start gap-3 rounded-2xl px-3 py-2.5 text-left transition-all duration-200 ${selected
        ? 'bg-primary-container text-on-primary-container shadow-[0_12px_24px_rgba(249,115,22,0.18)]'
        : 'bg-surface-container-low text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface'
        }`}
    >
      <span className="material-symbols-outlined mt-0.5 text-[18px]">{option.icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13px] font-semibold">{option.label}</span>
        <span className={`mt-0.5 block text-[11px] leading-5 ${selected ? 'text-on-primary-container opacity-80' : 'text-on-surface-variant'}`}>
          {option.description}
        </span>
        <span className="mt-2 flex items-center gap-1.5">
          {option.preview?.map((color) => (
            <span
              key={`${option.id}-${color}`}
              className="h-3 w-3 rounded-full border border-black/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.4)]"
              style={{ backgroundColor: color }}
            />
          ))}
        </span>
      </span>
    </button>
  );
}

export default function Shell({ user, onLogout }) {
  const location = useLocation();
  const navigate = useNavigate();
  const userMenuRef = useRef(null);
  const userLabel = user?.name || user?.email || 'Usuário';
  const userInitials = getUserInitials(user);
  const roleLabel = roleLabels[user?.role] || 'operação';
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isThemeMenuOpen, setIsThemeMenuOpen] = useState(false);
  const [themeMode, setThemeMode] = useState(() => {
    if (typeof window === 'undefined') return 'studio';
    return localStorage.getItem(THEME_STORAGE_KEY) || 'studio';
  });

  const currentThemeId = themeMode;
  const currentTheme = themeOptions.find((option) => option.id === currentThemeId) ?? themeOptions[0];
  const currentNavItem = useMemo(() => {
    const items = nav.flatMap((group) => group.items);
    return items.find((item) => matchesPath(location.pathname, item.to, item.end)) ?? items[0];
  }, [location.pathname]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    localStorage.setItem(THEME_STORAGE_KEY, themeMode);
    document.documentElement.dataset.studioTheme = currentThemeId;
    document.documentElement.style.colorScheme = currentThemeId === 'dark' ? 'dark' : 'light';
  }, [currentThemeId, themeMode]);

  useEffect(() => {
    if (!isUserMenuOpen) return undefined;

    const handlePointerDown = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
        setIsThemeMenuOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsUserMenuOpen(false);
        setIsThemeMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isUserMenuOpen]);

  function navigateFromMenu(path) {
    navigate(path);
    setIsUserMenuOpen(false);
    setIsThemeMenuOpen(false);
  }

  const accountActions = [
    {
      icon: 'account_circle',
      label: 'Meu perfil e conta',
      detail: 'Nome, senha e preferências pessoais.',
      path: '/configuracoes',
    },
    user?.role === 'ADMIN'
      ? {
        icon: 'group',
        label: 'Usuários e permissões',
        detail: 'Equipe, papéis e acessos do Estúdio.',
        path: '/usuarios',
      }
      : null,
    {
      icon: 'print',
      label: 'Dispositivos e impressão',
      detail: 'Impressoras, agentes e filas operacionais.',
      path: '/dispositivos',
    },
  ].filter(Boolean);

  const supportActions = [
    {
      icon: 'history',
      label: 'Histórico de impressão',
      detail: 'Últimos trabalhos, rastreio e reimpressão.',
      path: '/historico',
    },
    {
      icon: 'code',
      label: 'Documentação API',
      detail: 'Endpoints para integrações e automações.',
      path: '/documentacao',
    },
    {
      icon: 'help',
      label: 'Central de ajuda',
      detail: 'Guias rápidos, suporte e orientações.',
      path: '/ajuda',
    },
  ];

  return (
    <div className="studio-shell bg-background transition-colors duration-300">
      <aside className="studio-shell-sidebar fixed left-0 top-0 z-50 flex h-screen w-48 flex-col border-r backdrop-blur-xl">
        <div className="flex h-[64px] items-center border-b border-[color:var(--shell-border)] px-3">
          <div className="flex items-center gap-2.5">
            <div className="flex h-10 w-10 items-center justify-center rounded-[18px] bg-gradient-to-br from-sky-500 via-cyan-400 to-teal-300 text-white shadow-[0_14px_28px_-18px_rgba(14,165,233,0.9)]">
              <span className="material-symbols-outlined text-[20px]">sell</span>
            </div>
            <div className="min-w-0">
              <div className="studio-shell-brand-accent truncate text-[9px] font-semibold uppercase tracking-[0.16em]">EtiquetandoApp</div>
              <div className="studio-shell-muted truncate text-[12px]">{user?.tenantName || 'Nome da empresa'}</div>
            </div>
          </div>

        </div>

        <nav className="studio-sidebar-scroll flex-1 space-y-3.5 overflow-y-auto px-2.5 py-3.5">
          {nav.map((group) => (
            <div key={group.section}>
              {group.section && (
                <div className="studio-shell-section-label px-4 pb-1.5 text-[9px] font-medium uppercase tracking-[0.16em]">
                  {group.section}
                </div>
              )}
              <div className="space-y-0.5">
                {group.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) =>
                      `studio-shell-nav-item group relative mx-2.5 flex h-7.5 items-center gap-2 rounded-lg px-2.5 transition-all duration-150 ${isActive ? 'is-active' : ''}`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <span className={`studio-shell-nav-icon material-symbols-outlined shrink-0 text-[16px] transition-colors ${isActive ? 'is-active' : ''}`}>
                          {item.icon}
                        </span>
                        <span className={`studio-shell-nav-label truncate flex-1 text-[12px] leading-none transition-colors ${isActive ? 'font-medium is-active' : 'font-normal'}`}>
                          {item.label}
                        </span>
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

      </aside>

      <div className="pl-48">
        <header className="studio-shell-header fixed top-0 left-48 right-0 z-40 flex h-[64px] items-center justify-between border-b px-6 backdrop-blur-xl">
          <div className="flex min-w-0 items-center gap-3">

            <div className="studio-shell-search flex min-w-[280px] max-w-[560px] flex-1 items-center gap-2.5 rounded-full border px-3.5 py-1.5 shadow-[var(--shell-search-shadow)]">
              <span className="material-symbols-outlined text-[17px]">search</span>
              <span className="truncate text-[12px]">Pesquisar etiquetas, formatos, lotes, impressoras ou ordens...</span>
            </div>
          </div>

          <div className="flex items-center gap-2.5">
            {headerActions.map((action) => (
              <HeaderActionButton key={action.label} {...action} />
            ))}

            <div className="studio-shell-divider hidden h-7 w-px xl:block" />

            <div ref={userMenuRef} className="relative">
              <button
                type="button"
                title="Abrir perfil"
                aria-label="Abrir perfil"
                aria-expanded={isUserMenuOpen}
                onClick={() => {
                  setIsUserMenuOpen((value) => !value);
                  if (isUserMenuOpen) setIsThemeMenuOpen(false);
                }}
                className={`group relative flex h-9 w-9 items-center justify-center rounded-full transition-transform duration-200 ${isUserMenuOpen ? 'scale-[1.03]' : 'hover:scale-[1.03]'
                  }`}
              >
                <div className="relative">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[linear-gradient(135deg,#111827_0%,#374151_40%,#f97316_100%)] text-[12px] font-semibold text-white">
                    {userInitials}
                  </div>
                  <span className="studio-shell-status-dot absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 bg-emerald-500" />
                </div>
              </button>

              {isUserMenuOpen ? (
                <div className="studio-theme-options-scroll absolute right-0 top-full mt-3 max-h-[calc(100vh-88px)] w-[380px] overflow-y-auto overflow-x-hidden rounded-[28px] border border-surface-container-highest bg-surface-container-lowest/95 p-3 shadow-[0_34px_80px_rgba(15,23,42,0.20)] backdrop-blur-2xl">
                  <div className="rounded-[24px] border border-surface-container-highest bg-surface px-4 py-4 shadow-[0_14px_26px_rgba(15,23,42,0.08)]">
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[linear-gradient(135deg,#111827_0%,#374151_45%,#f97316_100%)] text-sm font-semibold text-white">
                          {userInitials}
                        </div>
                        <span className="studio-shell-status-dot absolute bottom-0 right-0 h-3.5 w-3.5 rounded-full border-2 bg-emerald-500" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-[13px] font-semibold text-on-surface">{userLabel}</div>
                        <div className="truncate text-[11px] text-on-surface-variant">{user?.email}</div>
                      </div>
                      <span className="rounded-full bg-primary-container px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-on-primary-container">
                        {roleLabel}
                      </span>
                    </div>

                    <div className="relative mt-4 overflow-visible">
                      <button
                        type="button"
                        onClick={() => setIsThemeMenuOpen((value) => !value)}
                        className={`flex w-full items-center justify-between gap-3 rounded-2xl px-3 py-3 text-left transition-colors ${isThemeMenuOpen
                          ? 'bg-surface-container text-on-surface'
                          : 'bg-surface-container-low text-on-surface hover:bg-surface-container'
                          }`}
                      >
                        <div className="min-w-0">
                          <div className="text-[11px] text-on-surface-variant">Tema ativo</div>
                          <div className="mt-1 text-[13px] font-semibold text-on-surface">{currentTheme.label}</div>
                          <div className="mt-0.5 text-[11px] text-on-surface-variant">Clique para alterar</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-surface-container-lowest text-on-surface shadow-[inset_0_1px_0_rgba(255,255,255,0.6)]">
                            <span className="material-symbols-outlined text-[20px]">{currentTheme.icon}</span>
                          </span>
                          <span className="material-symbols-outlined text-[18px] text-on-surface-variant">
                            {isThemeMenuOpen ? 'expand_less' : 'expand_more'}
                          </span>
                        </div>
                      </button>

                      {isThemeMenuOpen ? (
                        <div className="studio-theme-options-scroll mt-2 max-h-[320px] overflow-y-auto rounded-[20px] border border-surface-container-highest bg-surface-container-lowest/96 p-2 shadow-[0_18px_40px_rgba(15,23,42,0.10)] backdrop-blur-xl">
                          <div className="grid gap-2">
                            {themeOptions.map((option) => (
                              <ThemeOptionButton
                                key={option.id}
                                option={option}
                                selected={themeMode === option.id}
                                onClick={() => {
                                  setThemeMode(option.id);
                                  setIsThemeMenuOpen(false);
                                }}
                              />
                            ))}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className="mt-3 flex items-center justify-between gap-3 rounded-[24px] border border-surface-container-highest bg-surface px-4 py-3">
                    <div>
                      <div className="text-[13px] font-semibold text-on-surface">Sessão segura</div>
                      <div className="text-[11px] text-on-surface-variant">Seu acesso ao Estúdio está ativo neste navegador.</div>
                    </div>
                    <button
                      type="button"
                      onClick={async () => {
                        setIsUserMenuOpen(false);
                        setIsThemeMenuOpen(false);
                        await onLogout?.();
                      }}
                      className="inline-flex h-10 items-center gap-2 rounded-2xl bg-error-container px-4 text-[13px] font-semibold text-error transition-colors hover:bg-error-container/80"
                    >
                      <span className="material-symbols-outlined text-[18px]">logout</span>
                      Sair
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </header>

        <main className="studio-shell-main relative min-h-screen pt-[64px]">
          <div className="studio-shell-glow pointer-events-none absolute inset-x-0 top-0 h-64" />
          <div className="relative mx-auto flex h-full w-full max-w-container-max flex-col gap-section-gap px-6 py-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
